(ns common.opensearch.entity-indices
  (:require
   [clojure.string :as string]
   [common.schema.entity-type-search :as entity-type-search]))

;; Configuration SHARED BETWEEN FRONTEND AND BACKEND goes below
;; Configuration ONLY FOR THE BACKEND goes to common.opensearch.entity-indices/entity-type->index-config

;; :change-listening?
;; If true, a Datomic query that checks if there have been changes to entities of this type is executed on each sync loop.

;; :dedicated-index?
;; If true, entities of this :entity/type are indexed to both `universal` and `entity_<ENTITY_TYPE>` (e.g. `entity_company`) indices.

;; :extras-fields-fn
;; Returns the names of the fields under the :extras key in the OpenSearch document, that are allowed in queries to :search/search and :search/aggs endpoints.

;; :name-field
;; A dot-separated string path of the OpenSearch document field representing the name of the entity.

(def legacy-entity-type->index-config
  {:entity.type/account                       {:change-listening?         true
                                               :select-searchable?        true
                                               :dedicated-index?          true
                                               :extras-fields-fn          (fn [_tenant-schema] #{"extras.full-name" "extras.lower-case-display-name"})
                                               :universal-filters         {[:extras :active-user]     {:type "boolean"}
                                                                           [:extras :account-modules] {:type "text"}}
                                               :name-field                "extras.full-name"
                                               :show-in-universal-search? false}
   :entity.type/person                        {:change-listening?         true
                                               :dedicated-index?          true
                                               :select-searchable?        true
                                               :extras-fields-fn          (fn [_tenant-schema] #{"extras.full-name" "extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :show-in-universal-search? true}
   :entity.type/named-value                   {:change-listening?  true
                                               :dedicated-index?   true
                                               :select-searchable? true
                                               :name-field         "extras.full-name"
                                               :extras-fields-fn   (fn [_tenant-schema]
                                                                     #{"extras.full-name" "extras.lower-case-display-name" "extras.pipeline"})
                                               :universal-filters  {[:extras :pipeline]                {:type "number"}
                                                                    [:original :named-value/attribute] {:type "text"}
                                                                    [:original :common/archived?]      {:type "boolean"}}}
   :entity.type/company                       {:change-listening?         true
                                               :dedicated-index?          true
                                               :select-searchable?        true
                                               :extras-fields-fn          (constantly #{"extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :show-in-universal-search? true
                                               ;; Reverse of :investor/legal-entity - entities of type
                                               ;; investor whose own :investor/legal-entity points at
                                               ;; this company. Only consulted at the ROOT of a
                                               ;; company's own materialization (flatten.clj's
                                               ;; materialize-document, common.opensearch.access's
                                               ;; top-level accessible-logical-field-paths call) - a
                                               ;; company reached mid-walk from some other entity type
                                               ;; (e.g. via investor/legal-entity itself, or a future
                                               ;; oppo target-asset) never expands this, since it's not
                                               ;; a real field on company's own schema at all.
                                               ;;
                                               ;; No gate is declared alongside it, and that is the
                                               ;; point: a reverse ref flattens the SOURCE type's field
                                               ;; values into THIS type's document, so the source type's
                                               ;; own read gate has to be re-applied at query time or it
                                               ;; is simply lost - company documents are readable by
                                               ;; every active user in the tenant while investor
                                               ;; documents are gated on :module/fundraising, so without
                                               ;; that an account with no fundraising access could read
                                               ;; LP field data straight off the Companies index.
                                               ;; common.opensearch.access/accessible-logical-field-paths
                                               ;; now applies :entity.type/investor's own gate from
                                               ;; common.opensearch.read-gates by default, which is
                                               ;; where the earlier hand-written :required-modules
                                               ;; [:module/fundraising] here used to restate it. Derived
                                               ;; rather than restated, so the two cannot drift.
                                               :reverse-ref-fields        {:investor/_legal-entity
                                                                           {:entity-types [:entity.type/investor]}}}
   :entity.type/investor                      {:change-listening?         true
                                               :dedicated-index?          true
                                               :select-searchable?        true
                                               :associated-entity-fields  #{:investor/legal-entity}
                                               :extras-fields-fn          (constantly #{"extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :show-in-universal-search? true}
   :entity.type/real-estate                   {:change-listening?         true
                                               :dedicated-index?          true
                                               :select-searchable?        true
                                               :extras-fields-fn          (constantly #{"extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :show-in-universal-search? true}
   :entity.type/fof-asset                     {:change-listening?         true
                                               :dedicated-index?          true
                                               :select-searchable?        true
                                               :extras-fields-fn          (constantly #{"extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :show-in-universal-search? true}
   :entity.type/other-asset                   {:change-listening?         true
                                               :dedicated-index?          true
                                               :select-searchable?        true
                                               :extras-fields-fn          (constantly #{"extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :show-in-universal-search? true}
   :entity.type/fund                          {:change-listening?         true
                                               :dedicated-index?          true
                                               :select-searchable?        true
                                               :extras-fields-fn          (constantly #{"extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :show-in-universal-search? true}
   :entity.type/holding                       {:change-listening?            true
                                               :select-searchable?           true
                                               :dedicated-index?             true
                                               :associated-entity-fields     #{:holding/target-asset}
                                               :extras-fields-fn             (constantly #{"extras.lower-case-display-name"})
                                               :name-field                   "extras.full-name"
                                               :universal-trigger-attributes [:holding/name :holding/fund]
                                               :show-in-universal-search?    true}
   :entity.type/fund-commitment               {:change-listening?         true
                                               :select-searchable?        true
                                               :dedicated-index?          true
                                               :extras-fields-fn          (fn [_tenant-schema] #{"extras.full-name" "extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :show-in-universal-search? true}
   :entity.type/sample-entity                 {:dedicated-index?          true}
   :entity.type/kyc-profile                   {:change-listening?         true
                                               :select-searchable?        true
                                               :dedicated-index?          true
                                               :extras-fields-fn          (fn [_tenant-schema] #{"extras.investor-profile-name" "extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :show-in-universal-search? true}
   :entity.type/kyc-risk-category             {:change-listening?  true
                                               ;; TODO : universal indexing for risk category
                                               :select-searchable? true}
   :entity.type/stage                         {:change-listening?         true
                                               :select-searchable?        false
                                               :dedicated-index?          true
                                               :extras-fields-fn          (fn [_tenant-schema] #{"extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :universal-filters         {[:extras :pipeline] {:type "number"}}
                                               :show-in-universal-search? false}

   ; Todo these are not implemented on backend side!!
   :entity.type/survey-question               {:change-listening?  true
                                               :select-searchable? true
                                               :dedicated-index?   true}
   :entity.type/team                          {:change-listening?  true
                                               :select-searchable? true
                                               :universal-filters {[:extras :team-modules] {:type "text"}}}
   :entity.type/pipeline                      {:dedicated-index? true
                                               :name-field       "extras.full-name"}
   :entity.type/advisory-board-decision       {:dedicated-index? true}
   :entity.type/aifm-decision                 {:dedicated-index? true}
   :entity.type/aifm-exit-decision            {:dedicated-index? true}
   :entity.type/attachment                    {}
   :entity.type/check-list                    {:dedicated-index? true}
   :entity.type/contact-list                  {:dedicated-index? true}
   :entity.type/email                         {}
   :entity.type/email-template                {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/entity-association            {}
   :entity.type/esign-process                 {:dedicated-index? true}
   :entity.type/exit-opportunity-stage        {:change-listening?         true
                                               :select-searchable?        false
                                               :dedicated-index?          true
                                               :extras-fields-fn          (fn [_tenant-schema] #{"extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :universal-filters         {[:extras :pipeline] {:type "number"}}
                                               :show-in-universal-search? false}
   :entity.type/fee                           {:dedicated-index? true}
   :entity.type/feedback                      {}
   :entity.type/ic-decision                   {:dedicated-index? true}
   :entity.type/ic-exit-decision              {:dedicated-index? true}
   :entity.type/pm-approval-decision          {:dedicated-index? true}
   :entity.type/insider-group                 {:change-listening?  true
                                               :dedicated-index?   true
                                               :select-searchable? true}
   :entity.type/investment-reservation        {}
   :entity.type/investment-reservation-type   {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/involvement                   {}
   :entity.type/job                           {:dedicated-index? true}
   :entity.type/kpi-category                  {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/graph-company                 {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/graph-person                  {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/graph-fof-asset               {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/graph-fund                    {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/graph-reference               {}
   :entity.type/graph-meta                    {}
   :entity.type/graph-config                  {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/kpi-cycle                     {}
   :entity.type/kpi-member                    {}
   :entity.type/kpi-report                    {}
   :entity.type/kpi-report-answer             {}
   :entity.type/kpi-report-data               {}
   :entity.type/kpi-report-template           {}
   :entity.type/kyc-process                   {}
   :entity.type/lp-news-category              {}
   :entity.type/lp-news-item                  {:dedicated-index? true}
   :entity.type/lp-news-type                  {}
   :entity.type/lp-promotion                  {:dedicated-index? true}
   :entity.type/lp-report                     {}
   :entity.type/lp-report-holding             {}
   :entity.type/lp-report-investor-commitment {}
   :entity.type/metabase-dashboard            {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/named-view                    {:change-listening?  true
                                               ;; TODO - universal indexing
                                               :select-searchable? true}
   :entity.type/note                          {}
   :entity.type/note-call                     {}
   :entity.type/note-meeting                  {}
   :entity.type/note-message                  {}
   :entity.type/opportunity-stage             {:change-listening?         true
                                               :select-searchable?        false
                                               :dedicated-index?          true
                                               :extras-fields-fn          (fn [_tenant-schema] #{"extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :universal-filters         {[:extras :pipeline] {:type "number"}}
                                               :show-in-universal-search? false}
   :entity.type/pre-investment-check          {}
   :entity.type/pre-exit-check                {}
   :entity.type/risk                          {:dedicated-index? true}
   :entity.type/risk-holding                  {:dedicated-index? true}
   :entity.type/liability                     {:dedicated-index? true}
   :entity.type/login-page-config             {}
   :entity.type/sale-agreement                {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/sales-opportunity-stage       {:change-listening?         true
                                               :select-searchable?        false
                                               :dedicated-index?          true
                                               :extras-fields-fn          (fn [_tenant-schema] #{"extras.lower-case-display-name"})
                                               :name-field                "extras.full-name"
                                               :universal-filters         {[:extras :pipeline] {:type "number"}}
                                               :show-in-universal-search? false}
   :entity.type/scheduled-job                 {}
   :entity.type/screening-profile             {:dedicated-index? true}
   :entity.type/signatory                     {}
   :entity.type/survey-answer                 {}
   :entity.type/survey-process                {}
   ;; Indexed so survey cycles and profile overrides can pick a rotation period
   ;; through the search editor. Rotation periods are not bulk-synced to
   ;; new-grid clients, so a session-backed picker renders empty - see
   ;; ai/sync-model.md. `extras.survey-type-id` scopes the picker to the owning
   ;; survey type; without it every rotation period in the tenant is offered.
   :entity.type/rotation-period               {:change-listening?  true
                                               :select-searchable? true
                                               :universal-filters  {[:extras :survey-type-id] {:type "number"}}}
   :entity.type/survey-profile                {:change-listening?  true
                                               :select-searchable? true
                                               :universal-filters  {[:extras :survey-type-id] {:type "number"}
                                                                    [:extras :profile-status] {:type "text"}}}
   :entity.type/survey-question-option        {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/survey-question-ext-id        {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/survey-template               {:change-listening?  true
                                               :select-searchable? true
                                               :universal-filters  {[:extras :sub-template?]  {:type "boolean"}
                                                                    [:extras :survey-type-id] {:type "number"}}}
   :entity.type/survey-type                   {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/survey-block                  {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/task                          {}
   :entity.type/tenant                        {}
   :entity.type/tenant-kyc-profile            {;; TODO universal indexing
                                               }
   :entity.type/contract-kyc-profile          {;; TODO universal indexing
                                               }
   :entity.type/value-creation                {:dedicated-index? true}
   :entity.type/view                          {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/webentry                      {:change-listening?  true
                                               :select-searchable? true}
   :entity.type/role                          {:change-listening? true}})

(def entity-type->index-config
  (merge-with merge
              legacy-entity-type->index-config
              entity-type-search/registry-index-configs))

(def triggers-indexing-types
  (->> entity-type->index-config
       (keep (fn [[entity-type meta]]
               (when (or (:select-searchable? meta)
                         (:change-listening? meta))
                 entity-type)))
       (vec)))

(def select-indexed-entity-types
  (->> entity-type->index-config
       (keep (fn [[entity-type meta]]
               (when (:select-searchable? meta)
                 entity-type)))
       (vec)))

(def dedicated-index-entity-types
  (->> entity-type->index-config
       (keep (fn [[entity-type meta]]
               (when (:dedicated-index? meta)
                 entity-type)))
       (vec)))

(def universal-entity-types
  (->> entity-type->index-config
       (keep (fn [[entity-type meta]]
               (when (:show-in-universal-search? meta)
                 entity-type)))
       (vec)))

(def all-indexed-entity-types
  (->> (concat dedicated-index-entity-types
               triggers-indexing-types
               universal-entity-types)
       (set)))

(defn indexed-entity-type?
  [entity-type]
  (get all-indexed-entity-types entity-type))

(defn entity-index-name
  [entity-type]
  (str "entity_"
       (-> entity-type
           (string/replace "-" "_")
           (string/replace ":entity.type/" ""))))

(defn universal-field-paths
  [entity-type]
  (->> (get entity-type->index-config entity-type)
       :universal-filters
       (map key)))

(defn is-search-indexed?
  [entity-type]
  (some #{entity-type} select-indexed-entity-types))

(defn associated-entity-fields
  [entity-type]
  (get-in entity-type->index-config [entity-type :associated-entity-fields]))
